import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Badge, Alert, Row, Col } from 'react-bootstrap';
import StockAlertsTable from './StockAlertsTable';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
    getTelegramAlerts, updateTelegramAlerts, sendTelegramAlertTest, sendTelegramBriefingNow,
} from '../../../services/SettingsService';

const TOGGLES = ['new_order', 'low_stock', 'overdue', 'briefing', 'commands'];
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

const toForm = (s) => ({
    enabled: { ...s.enabled },
    stock_threshold: s.stock_threshold,
    min_threshold: s.min_threshold,
    restock_days: s.restock_days,
    safety_days: s.safety_days,
    overdue_days: s.overdue_days,
    overdue_start_hour: s.overdue_start_hour,
    overdue_end_hour: s.overdue_end_hour,
    overdue_interval_hours: s.overdue_interval_hours,
    briefing_time: s.briefing_time,
    chat_id: s.chat_id || '',
});

const intField = (label, min, max) => Yup.number().typeError(label).integer(label).min(min, label).max(max, label).required(label);

export default function TelegramAlerts() {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [overview, setOverview] = useState(null);
    const [busy, setBusy] = useState('');

    useEffect(() => {
        (async () => {
            try {
                const res = await getTelegramAlerts();
                setOverview(res.data);
            } catch {
                toast.error(t('telegramAlerts.errors.load'));
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const schema = Yup.object({
        stock_threshold: intField(t('telegramAlerts.errors.number'), 0, 100000),
        min_threshold: intField(t('telegramAlerts.errors.number'), 0, 100000),
        restock_days: intField(t('telegramAlerts.errors.number'), 0, 60),
        safety_days: intField(t('telegramAlerts.errors.number'), 0, 60),
        overdue_days: intField(t('telegramAlerts.errors.number'), 1, 60),
        overdue_start_hour: intField(t('telegramAlerts.errors.hour'), 0, 23),
        overdue_end_hour: intField(t('telegramAlerts.errors.hour'), 0, 23)
            .min(Yup.ref('overdue_start_hour'), t('telegramAlerts.errors.hourOrder')),
        overdue_interval_hours: intField(t('telegramAlerts.errors.number'), 1, 24),
        briefing_time: Yup.string().matches(TIME_RE, t('telegramAlerts.errors.time')).required(t('telegramAlerts.errors.time')),
        chat_id: Yup.string().matches(/^(-?\d{1,20})?$/, t('telegramAlerts.errors.chatId')),
    });

    const handleSave = async (values, { setSubmitting }) => {
        try {
            const res = await updateTelegramAlerts(values);
            setOverview(res.data);
            toast.success(t('telegramAlerts.success.saved'));
        } catch (err) {
            toast.error(err.response?.data?.error || t('telegramAlerts.errors.save'));
        } finally {
            setSubmitting(false);
        }
    };

    const run = async (key, fn, okMsg, failMsg) => {
        setBusy(key);
        try {
            await fn();
            toast.success(okMsg);
        } catch (err) {
            toast.error(err.response?.data?.error || failMsg);
        } finally {
            setBusy('');
        }
    };

    const shopify = overview?.shopify;
    const telegram = overview?.telegram;

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <Card>
                        <Card.Header>
                            <div>
                                <h4 className="heading mb-0">{t('telegramAlerts.title')}</h4>
                                <span className="text-muted">{t('telegramAlerts.subtitle')}</span>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            {loading || !overview ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status" />
                                </div>
                            ) : (
                                <Formik initialValues={toForm(overview.settings)} validationSchema={schema} onSubmit={handleSave} enableReinitialize>
                                    {({ values, errors, handleSubmit, handleChange, setFieldValue, isSubmitting }) => (
                                        <Form noValidate onSubmit={handleSubmit}>
                                            <div className="d-flex flex-wrap gap-2 mb-4">
                                                <Badge bg={telegram.configured ? 'success' : 'danger'}>
                                                    {telegram.configured ? t('telegramAlerts.status.telegramOk') : t('telegramAlerts.status.telegramMissing')}
                                                </Badge>
                                                <Badge bg={shopify.connected ? 'success' : 'warning'} text={shopify.connected ? undefined : 'dark'}>
                                                    {shopify.connected ? t('telegramAlerts.status.shopifyOk') : t('telegramAlerts.status.shopifyNo')}
                                                </Badge>
                                            </div>
                                            {!shopify.connected && (
                                                <Alert variant="warning">{t('telegramAlerts.status.shopifyHint')}</Alert>
                                            )}

                                            <h5 className="mb-3">{t('telegramAlerts.sections.types')}</h5>
                                            {TOGGLES.map((key) => (
                                                <Form.Check
                                                    key={key}
                                                    type="switch"
                                                    id={`telegram-toggle-${key}`}
                                                    className="mb-1"
                                                    label={t(`telegramAlerts.toggles.${key}`)}
                                                    checked={!!values.enabled[key]}
                                                    onChange={(e) => setFieldValue(`enabled.${key}`, e.target.checked)}
                                                />
                                            ))}
                                            <p className="text-muted small mb-4">{t('telegramAlerts.togglesHint')}</p>

                                            <h5 className="mb-3">{t('telegramAlerts.sections.stock')}</h5>
                                            <Form.Group className="mb-3" controlId="telegram-threshold">
                                                <Form.Label>{t('telegramAlerts.fields.threshold')}</Form.Label>
                                                <Form.Control type="number" min="0" name="stock_threshold" value={values.stock_threshold}
                                                    onChange={handleChange} isInvalid={!!errors.stock_threshold} className="telegram-alerts-narrow" />
                                                <Form.Control.Feedback type="invalid">{errors.stock_threshold}</Form.Control.Feedback>
                                                <Form.Text className="text-muted">{t('telegramAlerts.fields.thresholdHint')}</Form.Text>
                                            </Form.Group>
                                            <Row className="g-3 mb-1">
                                                {[['min_threshold', 'minThreshold'], ['restock_days', 'restockDays'], ['safety_days', 'safetyDays']].map(([name, label]) => (
                                                    <Col xs={6} md={3} key={name}>
                                                        <Form.Group controlId={`telegram-${name}`}>
                                                            <Form.Label>{t(`telegramAlerts.fields.${label}`)}</Form.Label>
                                                            <Form.Control type="number" min="0" name={name} value={values[name]} onChange={handleChange} isInvalid={!!errors[name]} />
                                                            <Form.Control.Feedback type="invalid">{errors[name]}</Form.Control.Feedback>
                                                        </Form.Group>
                                                    </Col>
                                                ))}
                                            </Row>
                                            <Form.Text className="text-muted d-block mb-4">{t('telegramAlerts.fields.autoHint')}</Form.Text>

                                            <h5 className="mb-3">{t('telegramAlerts.sections.overdue')}</h5>
                                            <Row className="g-3 mb-1">
                                                {[['overdue_days', 'overdueDays', 1], ['overdue_start_hour', 'overdueStart', 0], ['overdue_end_hour', 'overdueEnd', 0], ['overdue_interval_hours', 'overdueInterval', 1]].map(([name, label, min]) => (
                                                    <Col xs={6} md={3} key={name}>
                                                        <Form.Group controlId={`telegram-${name}`}>
                                                            <Form.Label>{t(`telegramAlerts.fields.${label}`)}</Form.Label>
                                                            <Form.Control type="number" min={min} name={name} value={values[name]} onChange={handleChange} isInvalid={!!errors[name]} />
                                                            <Form.Control.Feedback type="invalid">{errors[name]}</Form.Control.Feedback>
                                                        </Form.Group>
                                                    </Col>
                                                ))}
                                            </Row>
                                            <Form.Text className="text-muted d-block mb-4">{t('telegramAlerts.fields.overdueHint')}</Form.Text>

                                            <h5 className="mb-3">{t('telegramAlerts.sections.briefing')}</h5>
                                            <Form.Group className="mb-4" controlId="telegram-briefing-time">
                                                <Form.Label>{t('telegramAlerts.fields.briefingTime')}</Form.Label>
                                                <Form.Control type="time" name="briefing_time" value={values.briefing_time} onChange={handleChange}
                                                    isInvalid={!!errors.briefing_time} className="telegram-alerts-narrow" />
                                                <Form.Control.Feedback type="invalid">{errors.briefing_time}</Form.Control.Feedback>
                                            </Form.Group>

                                            <h5 className="mb-3">{t('telegramAlerts.sections.telegram')}</h5>
                                            <Form.Group className="mb-3" controlId="telegram-chat-id">
                                                <Form.Label>{t('telegramAlerts.fields.chatId')}</Form.Label>
                                                <Form.Control name="chat_id" value={values.chat_id} onChange={handleChange} isInvalid={!!errors.chat_id}
                                                    placeholder={telegram.chat_id_from_env ? t('telegramAlerts.fields.chatIdEnv') : ''} />
                                                <Form.Control.Feedback type="invalid">{errors.chat_id}</Form.Control.Feedback>
                                                <Form.Text className="text-muted">{t('telegramAlerts.fields.chatIdHint')}</Form.Text>
                                            </Form.Group>

                                            <Button type="submit" variant="primary" disabled={isSubmitting} className="me-3">
                                                {isSubmitting ? t('common.saving') : t('common.save')}
                                            </Button>

                                            <hr className="my-4" />
                                            <div className="d-flex align-items-center gap-3 flex-wrap">
                                                <Button variant="outline-primary" disabled={!!busy}
                                                    onClick={() => run('test', sendTelegramAlertTest, t('telegramAlerts.success.testSent'), t('telegramAlerts.errors.test'))}>
                                                    {busy === 'test' ? t('telegramAlerts.sending') : t('telegramAlerts.sendTest')}
                                                </Button>
                                                <Button variant="outline-primary" disabled={!!busy}
                                                    onClick={() => run('briefing', sendTelegramBriefingNow, t('telegramAlerts.success.briefingSent'), t('telegramAlerts.errors.briefing'))}>
                                                    {busy === 'briefing' ? t('telegramAlerts.sending') : t('telegramAlerts.sendBriefing')}
                                                </Button>
                                                <span className="text-muted small">{t('telegramAlerts.buttonsHint')}</span>
                                            </div>
                                        </Form>
                                    )}
                                </Formik>
                            )}
                        </Card.Body>
                    </Card>
                    {overview && <StockAlertsTable shopifyConnected={overview.shopify.connected} />}
                </div>
            </div>
        </div>
    );
}
